import React, { useState, useEffect, useCallback } from "react";
import "./Employees.css";
import NewEmployeeForm from "../Forms/NewEmployeeForm";
import Layout from "../UI/Layout";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../firebase";

// ── Snackbar Component ─────────────────────────────────
const Snackbar = ({ message, type, visible }) => (
  <div className={`snackbar snackbar-${type} ${visible ? "snackbar-show" : ""}`}>
    <i className={`fas ${type === "success" ? "fa-check-circle" : "fa-exclamation-circle"}`}></i>
    {message}
  </div>
);

// ── Helper: format date string (YYYY-MM-DD) to readable ─
const formatDate = (val) => {
  if (!val) return "—";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  const [y, m, d] = val.split("-");
  return `${m}/${d}/${y}`;
};

// view: "list" | "detail" | "add"
const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [snackbar, setSnackbar] = useState({ message: "", type: "success", visible: false });

  // ── Snackbar helper ────────────────────────────────────
  const showSnackbar = useCallback((message, type = "success") => {
    setSnackbar({ message, type, visible: true });
    setTimeout(() => setSnackbar((s) => ({ ...s, visible: false })), 3500);
  }, []);

  // ── Fetch all employees on mount ───────────────────────
  useEffect(() => {
    const fetchEmployees = async () => {
      setLoading(true);
      try {
        const snapshot = await getDocs(collection(db, "employees"));
        const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setEmployees(data.sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      } catch (err) {
        console.error("Error fetching employees:", err);
        showSnackbar("Failed to load employees.", "error");
      }
      setLoading(false);
    };
    fetchEmployees();
  }, [showSnackbar]);

  // ── After NewEmployeeForm saves to Firestore, append to local state ──
  const handleAddEmployee = (newEmployee) => {
    setEmployees((prev) => [...prev, newEmployee]);
    setView("list");
    showSnackbar("Employee added successfully!");
  };

  const filteredEmployees = employees.filter((emp) =>
    emp.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCardClick = (employee) => {
    setSelectedEmployee(employee);
    setIsEditing(false);
    setView("detail");
  };

  const handleBack = () => {
    setSelectedEmployee(null);
    setIsEditing(false);
    setView("list");
  };

  const handleEditClick = () => {
    setEditData({ ...selectedEmployee });
    setIsEditing(true);
  };

  const handleEditChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  // ── Save edits to Firestore ────────────────────────────
  const handleSave = async () => {
    setUpdating(true);
    try {
      const { id, ...dataToSave } = editData;
      await updateDoc(doc(db, "employees", id), dataToSave);
      setEmployees((prev) => prev.map((emp) => (emp.id === id ? editData : emp)));
      setSelectedEmployee(editData);
      setIsEditing(false);
      showSnackbar("Employee updated successfully!");
    } catch (err) {
      console.error("Error updating employee:", err);
      showSnackbar("Failed to update employee.", "error");
    }
    setUpdating(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditData({});
  };

  // ── Delete employee from Firestore ─────────────────────
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteDoc(doc(db, "employees", selectedEmployee.id));
      setEmployees((prev) => prev.filter((emp) => emp.id !== selectedEmployee.id));
      showSnackbar(`${selectedEmployee.name} has been deleted.`, "success");
      setConfirmDelete(false);
      handleBack();
    } catch (err) {
      console.error("Error deleting employee:", err);
      showSnackbar("Failed to delete employee.", "error");
    }
    setDeleting(false);
  };

  // ── Field definitions ──────────────────────────────────
  const fields = [
    { key: "name", label: "Name", icon: "fa-user", fullWidth: false },
    { key: "email", label: "Email", icon: "fa-envelope", fullWidth: false },
    { key: "phone", label: "Phone", icon: "fa-phone", fullWidth: false },
    { key: "fuelCard", label: "Fuel Card", icon: "fa-gas-pump", fullWidth: false },
    { key: "address", label: "Address", icon: "fa-map-marker-alt", fullWidth: true },
    { key: "city", label: "City", icon: "fa-city", fullWidth: false },
    { key: "state", label: "State", icon: "fa-flag", fullWidth: false },
    { key: "zip", label: "Zip Code", icon: "fa-mail-bulk", fullWidth: false },
  ];

  const cdlFields = [
    { key: "cdlNumber",       label: "CDL Driver's License #",                      icon: "fa-id-card",      fullWidth: false, type: "text" },
    { key: "cdlIssueDate",    label: "CDL Issue Date",                               icon: "fa-calendar-alt", fullWidth: false, type: "date" },
    { key: "medCertNumber",   label: "Med Examiner's Certificate Reg. #",        icon: "fa-file-medical", fullWidth: false, type: "text" },
    { key: "medCertIssueDate",label: "Med Examiner's Cert. Issue Date",    icon: "fa-calendar-alt", fullWidth: false, type: "date" },
  ];

  return (
    <Layout activePage="employees">
      <div className="employees-page">
        <Snackbar {...snackbar} />

        {/* Header */}
        <div className="emp-header">
          <h1 className="emp-title">Employees</h1>
          {view === "list" && (
            <div className="emp-controls">
              <div className="emp-search-wrap">
                <i className="fas fa-search emp-search-icon"></i>
                <input
                  type="text"
                  placeholder="Search employees..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="emp-search"
                />
              </div>
              <button className="emp-add-btn" onClick={() => setView("add")}>
                <i className="fas fa-plus"></i> Employee
              </button>
            </div>
          )}
        </div>

        {/* Add Form */}
        {view === "add" && (
          <NewEmployeeForm onSubmit={handleAddEmployee} onClose={handleBack} />
        )}

        {/* Detail View */}
        {view === "detail" && selectedEmployee && (
          <div className="emp-detail">
            <button className="back-btn" onClick={handleBack}>
              <i className="fas fa-arrow-left"></i> Back to Employees
            </button>

            <div className="detail-card">
              <div className="detail-top">
                <div className="detail-avatar">{selectedEmployee.name?.charAt(0)}</div>
                <h2>{selectedEmployee.name}</h2>
                <span className="detail-email">{selectedEmployee.email}</span>
              </div>

              {!isEditing && (
                <div className="detail-actions-top">
                  <button className="edit-btn" onClick={handleEditClick}>
                    <i className="fas fa-pen"></i> Edit
                  </button>
                </div>
              )}

              {/* ── General Info Fields ── */}
              <div className="detail-fields">
                {fields.map(({ key, label, icon, fullWidth }) => (
                  <div key={key} className={`detail-field${fullWidth ? " full-width" : ""}`}>
                    <label><i className={`fas ${icon}`}></i> {label}</label>
                    {isEditing ? (
                      <input className="field-input" type="text" name={key}
                        value={editData[key] || ""} onChange={handleEditChange} />
                    ) : (
                      <div className="field-value">{selectedEmployee[key] || "—"}</div>
                    )}
                  </div>
                ))}
              </div>

              {/* ── CDL & Certification Section ── */}
              <div className="detail-section-divider">
                <i className="fas fa-id-card"></i> CDL &amp; Certification
              </div>
              <div className="detail-fields">
                {cdlFields.map(({ key, label, icon, fullWidth, type }) => (
                  <div key={key} className={`detail-field${fullWidth ? " full-width" : ""}`}>
                    <label><i className={`fas ${icon}`}></i> {label}</label>
                    {isEditing ? (
                      <input
                        className="field-input"
                        type={type}
                        name={key}
                        value={editData[key] || ""}
                        onChange={handleEditChange}
                      />
                    ) : (
                      <div className="field-value">
                        {type === "date"
                          ? formatDate(selectedEmployee[key])
                          : selectedEmployee[key] || "—"}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {isEditing && (
                <div className="detail-save-row">
                  <button className="btn-cancel-edit" onClick={handleCancelEdit} disabled={updating}>Cancel</button>
                  <button className="btn-save" onClick={handleSave} disabled={updating}>
                    {updating
                      ? <><i className="fas fa-spinner fa-spin"></i> Saving...</>
                      : <><i className="fas fa-check"></i> Save Changes</>}
                  </button>
                </div>
              )}

              {/* ── Delete Section ── */}
              {!isEditing && (
                <div className="detail-delete-section">
                  {!confirmDelete ? (
                    <button className="btn-delete-emp" onClick={() => setConfirmDelete(true)}>
                      <i className="fas fa-trash-alt"></i> Delete Employee
                    </button>
                  ) : (
                    <div className="delete-confirm-box">
                      <p className="delete-confirm-text">
                        <i className="fas fa-exclamation-triangle"></i>
                        Are you sure you want to delete <strong>{selectedEmployee.name}</strong>? This action cannot be undone.
                      </p>
                      <div className="delete-confirm-actions">
                        <button className="btn-cancel-delete" onClick={() => setConfirmDelete(false)} disabled={deleting}>
                          Cancel
                        </button>
                        <button className="btn-confirm-delete" onClick={handleDelete} disabled={deleting}>
                          {deleting
                            ? <><i className="fas fa-spinner fa-spin"></i> Deleting...</>
                            : <><i className="fas fa-trash-alt"></i> Yes, Delete</>}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Table */}
        {view === "list" && (
          <div className="emp-table-wrapper">
            {loading ? (
              <div className="trucks-loading">
                <i className="fas fa-spinner fa-spin"></i> Loading employees...
              </div>
            ) : (
              <div className="emp-table-scroll">
                <table className="emp-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Fuel Card</th>
                      <th>CDL #</th>
                      <th>CDL Issue Date</th>
                      <th>Med Cert #</th>
                      <th>Med Cert Issue Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.map((emp, index) => (
                      <tr key={emp.id} onClick={() => handleCardClick(emp)}>
                        <td className="row-index">{index + 1}</td>
                        <td className="td-emp-name">
                          <div className="emp-name-cell">
                            <div className="emp-avatar-sm">{emp.name?.charAt(0)}</div>
                            {emp.name}
                          </div>
                        </td>
                        <td className="td-emp-phone">{emp.phone || "—"}</td>
                        <td className="td-emp-fuel">{emp.fuelCard || "—"}</td>
                        <td className="td-emp-cdl">{emp.cdlNumber || "—"}</td>
                        <td className="td-emp-cdlissue">{formatDate(emp.cdlIssueDate)}</td>
                        <td className="td-emp-medcert">{emp.medCertNumber || "—"}</td>
                        <td className="td-emp-medcertissue">{formatDate(emp.medCertIssueDate)}</td>
                      </tr>
                    ))}

                    {filteredEmployees.length === 0 && (
                      <tr>
                        <td colSpan="8" className="no-results">No employees found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </div>
    </Layout>
  );
};

export default Employees;